import { useId, useRef, type MouseEvent } from "react";
import { useInertRef } from "@hooks/useInertRef";
import {
    ActivityRow,
    type ActivityListItem,
} from "@design-system/ActivityList/ActivityList";
import { BOARD_CARD_ATTRIBUTE } from "./boardAttributes";
import { useBoardCardExpansion } from "./boardCardExpansion";
import { useBoardCardTabStop } from "./boardCardFocus";
import {
    BOARD_DRAG_THROUGH_ATTRIBUTE,
    BOARD_NO_DRAG_ATTRIBUTE,
    isFromInteractiveDescendant,
} from "./boardSensors";
import styles from "./BoardCardActivity.module.scss";

export type BoardCardActivityItem = ActivityListItem;

const MAX_ITEMS = 7;
const DRAG_THROUGH_PROPS = { [BOARD_DRAG_THROUGH_ATTRIBUTE]: "" };
// A click on the rows is the drawer's, not the card's; a press and move
// still drags the card.
const TIMELINE_PROPS = {
    [BOARD_NO_DRAG_ATTRIBUTE]: "",
    ...DRAG_THROUGH_PROPS,
};

const ActivityTimeline = ({
    items: [newest, ...older],
}: {
    items: readonly [BoardCardActivityItem, ...BoardCardActivityItem[]];
}) => {
    const rowsId = useId();
    const toggleRef = useRef<HTMLButtonElement>(null);
    const { isExpanded, toggle } = useBoardCardExpansion();
    const tabStopProps = useBoardCardTabStop();
    const olderRef = useInertRef(!isExpanded);
    // A click anywhere but a control in the rows toggles, then hands focus
    // back to the card, so the board's arrows work on.
    const handleClick = (event: MouseEvent<HTMLElement>) => {
        if (
            event.target !== toggleRef.current &&
            isFromInteractiveDescendant(event)
        ) {
            return;
        }
        toggle();
        if (!toggleRef.current?.matches(":focus-visible")) {
            event.currentTarget
                .closest<HTMLElement>(`[${BOARD_CARD_ATTRIBUTE}]`)
                ?.focus({ preventScroll: true });
        }
    };

    return (
        // The button under the rows is the keyboard's way in; the rows stay
        // on top so their links and truncation tooltips get the pointer.
        <span
            {...TIMELINE_PROPS}
            className={styles.timeline}
            onClick={handleClick}
        >
            <button
                ref={toggleRef}
                {...tabStopProps}
                {...DRAG_THROUGH_PROPS}
                type="button"
                className={styles.toggle}
                aria-expanded={isExpanded}
                aria-label="Activity"
                aria-controls={rowsId}
            />
            <span id={rowsId} className={styles.rows}>
                <ActivityRow item={newest} hasConnector />
                <span className={styles.older} ref={olderRef}>
                    <span className={styles.olderClip}>
                        <span className={styles.olderList}>
                            {older.map((item, index) => (
                                <ActivityRow
                                    key={item.id}
                                    item={item}
                                    hasConnector={index < older.length - 1}
                                />
                            ))}
                        </span>
                    </span>
                </span>
            </span>
        </span>
    );
};

/**
 * Recent activity for `BoardCard`'s `drawer`, newest first; the first seven
 * items show. One item is a plain row, part of the card: clicking it opens
 * the card. More are a timeline that shows the newest row with a connector
 * running out under the drawer's edge, and toggles open to all of them on
 * click, without opening the card. Links in the rows follow their own target
 * instead; give them `useBoardCardTabStop()`'s props. The board keeps each
 * card's expanded state while it's open (`useBoardCardExpansion`). Either way
 * a press and move anywhere but a link drags the card. Pass it only with
 * items; no drawer is `drawer={undefined}`.
 */
export const BoardCardActivity = ({
    items,
}: {
    items: readonly BoardCardActivityItem[];
}) => {
    if (items.length === 0) {
        return null;
    }
    const [newest, ...older] = items.slice(0, MAX_ITEMS);
    if (older.length === 0) {
        return (
            <span className={styles.rows}>
                <ActivityRow item={newest} hasConnector={false} />
            </span>
        );
    }
    return <ActivityTimeline items={[newest, ...older]} />;
};
